import { useSyncedRef } from "@react-hookz/web";
import { getUnixTime } from "date-fns";
import React, { useEffect } from "react";
import { useControlledPromise } from "scmp-sdk";
import { useSdkContext } from "scmp-sdk/components/contexts";
import type { TooltipItem } from "scmp-sdk/components/dashboard-widget/tooltips";

import { useCookieValue, useFallbackStorageValue } from "shared/hooks";

import { LoginReminderTooltip } from "./component";

export const useLoginReminderTooltipRender = () => {
  const { hasAcceptedGDPR, user } = useSdkContext();

  // The value is set by account sdk: https://project.scmp.tech/search?scope=blobs&search=last_logout_timestamp
  const [lastLogoutTimestamp] = useCookieValue("last_logout_timestamp");

  const {
    remove: removeReminderState,
    set: setReminderState,
    value: reminderState,
  } = useFallbackStorageValue<{ intervalTime: number; shown: boolean }[]>("loginReminderData");
  const syncedReminderState = useSyncedRef(reminderState);

  // Reset the data when user has logged in
  useEffect(() => {
    if (user) {
      removeReminderState();
    }
  }, [removeReminderState, user]);

  const { promise, reject, resolve } = useControlledPromise<TooltipItem>();

  useEffect(() => {
    if (!hasAcceptedGDPR || lastLogoutTimestamp === null || user) return reject.current();

    const reminderState = syncedReminderState.current ?? [
      {
        // 30 minutes
        intervalTime: 1800,
        shown: false,
      },
      {
        // 1 day
        intervalTime: 86_400,
        shown: false,
      },
      {
        // 30 days
        intervalTime: 172_800,
        shown: false,
      },
    ];

    const currentUnixTime = getUnixTime(new Date());
    const parsedLastLogoutTimestamp = Number.parseInt(lastLogoutTimestamp ?? "0");
    for (const [index, state] of reminderState.entries()) {
      if (currentUnixTime - parsedLastLogoutTimestamp >= state.intervalTime && !state.shown) {
        const updatedReminderState = () => {
          setReminderState(() => {
            reminderState[index].shown = true;
            return reminderState;
          });
        };
        return resolve.current({
          node: (
            <LoginReminderTooltip
              onImpression={() => {
                updatedReminderState();
              }}
            />
          ),
          onChangeUrl: () => {
            updatedReminderState();
          },
        });
      }
    }

    return reject.current();
  }, [
    lastLogoutTimestamp,
    reject,
    resolve,
    setReminderState,
    syncedReminderState,
    hasAcceptedGDPR,
    user,
  ]);

  return { loginReminderTooltipRender: promise };
};
